import type { BlockType } from "@/lib/lesson-content/schema";
import { DEFAULT_DATA_SANDBOX_DATASETS } from "./dataSandboxDefaults";
import { emptyTable, tableToMarkdown } from "@/lib/lesson-content/table-markdown";
import { MEANING_MAP_EXAMPLE, SPAM_FILTER_EXAMPLE, TRAIN_MODEL_EXAMPLE } from "@/lib/ile/sims/examples";
import {
  CAPABILITY_CARD_EXAMPLE,
  CARD_GRID_EXAMPLE,
  COLOR_SORT_EXAMPLE,
  COMPARE_COLUMNS_EXAMPLE,
  DECISION_TREE_EXAMPLE,
  PROJECT_EXPORT_EXAMPLE,
  PROMPT_BUILDER_EXAMPLE,
  QUICK_QUESTION_EXAMPLE,
  SCENARIO_SORT_EXAMPLE,
  SPOT_THE_ERRORS_EXAMPLE,
  SLIDER_JUDGE_EXAMPLE,
  SLIDER_RATE_EXAMPLE,
  TILE_REVEAL_EXAMPLE,
} from "@/lib/ile/block-examples";

/**
 * Loose working shape for a block while it's being edited in the canvas. Fields
 * fill in progressively, so we don't hold the strict `LessonBlock` type here —
 * the strict contract is enforced once, on save, via `safeParseLessonContent`.
 */
export type EditorBlock = {
  id: string;
  type: BlockType;
  required_to_advance: boolean;
  personalized: boolean;
  data: Record<string, unknown>;
};

/** Block types that have a form editor implemented. */
export const SUPPORTED_BLOCK_TYPES = [
  "rich_text",
  "image",
  "image_hotspot",
  "flip_cards",
  "accordion_tabs",
  "stepped_timeline",
  "wheel_diagram",
  "nested_layers",
  "format_switcher",
  "image_switcher",
  "vertical_roadmap",
  "add_next_layer",
  "context_window_slider",
  "cliche_highlighter",
  "tokenizer_playground",
  "be_the_model",
  "workflow_simulator",
  "agent_simulator",
  "dual_chatbot",
  "data_sandbox",
  "checklist",
  "free_response",
  "quick_question",
  "card_grid",
  "compare_columns",
  "tile_reveal",
  "color_sort",
  "slider_set",
  "scenario_sort",
  "spot_the_errors",
  "decision_tree",
  "prompt_builder",
  "project_export",
  "filter_compare",
  "train_model",
  "meaning_map",
  "mcq",
  "categorization",
  "sequencing",
  "fill_blank",
  "workplace_scenario",
] as const;

/** Human labels for the block palette / cards. */
export const BLOCK_LABELS: Record<BlockType, string> = {
  rich_text: "Rich text",
  image: "Image",
  image_hotspot: "Image hotspot",
  flip_cards: "Flip cards",
  accordion_tabs: "Accordion / Tabs",
  stepped_timeline: "Stepped timeline",
  wheel_diagram: "Wheel diagram",
  nested_layers: "Nested layers",
  format_switcher: "Format switcher",
  image_switcher: "Image switcher",
  vertical_roadmap: "Vertical roadmap",
  add_next_layer: "Add the next layer",
  context_window_slider: "Context window slider",
  cliche_highlighter: "Cliché highlighter",
  tokenizer_playground: "Tokenizer playground",
  be_the_model: "Be the model",
  workflow_simulator: "Workflow simulator",
  agent_simulator: "Agent simulator",
  dual_chatbot: "Two chatbots",
  data_sandbox: "Data sandbox",
  checklist: "Checklist",
  free_response: "Written answer",
  quick_question: "Quick question",
  card_grid: "Card grid",
  compare_columns: "Compare columns",
  tile_reveal: "Tap-to-reveal tiles",
  color_sort: "Colour sort",
  slider_set: "Sliders",
  scenario_sort: "Scenario sort",
  spot_the_errors: "Spot the errors",
  decision_tree: "Decision tree",
  prompt_builder: "Prompt builder",
  project_export: "Download my work",
  filter_compare: "Rule vs learned filter (simulator)",
  train_model: "Train your own model (simulator)",
  meaning_map: "Meaning map (simulator)",
  mcq: "Multiple choice",
  categorization: "Categorization",
  sequencing: "Sequencing",
  fill_blank: "Fill in the blanks",
  workplace_scenario: "Workplace scenario (personalized)",
};

/**
 * One entry in the "+ Block" menu. Blocks with more than one mode list each mode as its own
 * entry, so an author picks "Tabs" or "Find the hidden spots" up front instead of having to
 * know there is a switch inside the block's form.
 */
export type BlockMenuItem = {
  key: string;
  type: (typeof SUPPORTED_BLOCK_TYPES)[number];
  label: string;
  /** Merged over the type's default data. */
  data?: Record<string, unknown>;
};

export const BLOCK_MENU: BlockMenuItem[] = [
  { key: "rich_text", type: "rich_text", label: "Rich text" },
  {
    key: "table",
    type: "rich_text",
    label: "Table",
    data: { markdown: tableToMarkdown(emptyTable()) },
  },
  { key: "image", type: "image", label: "Image" },
  { key: "image_hotspot", type: "image_hotspot", label: "Image hotspot (explore)" },
  { key: "image_hotspot_find", type: "image_hotspot", label: "Image hotspot (find the spots)", data: { mode: "find" } },
  { key: "flip_cards", type: "flip_cards", label: "Flip cards" },
  { key: "accordion", type: "accordion_tabs", label: "Accordion" },
  { key: "tabs", type: "accordion_tabs", label: "Tabs", data: { display: "tabs" } },
  { key: "stepped_timeline", type: "stepped_timeline", label: "Stepped timeline" },
  { key: "wheel_diagram", type: "wheel_diagram", label: "Wheel diagram" },
  { key: "nested_layers", type: "nested_layers", label: "Nested layers" },
  { key: "format_switcher", type: "format_switcher", label: "Format switcher" },
  { key: "image_switcher", type: "image_switcher", label: "Image switcher" },
  { key: "vertical_roadmap", type: "vertical_roadmap", label: "Vertical roadmap" },
  { key: "add_next_layer", type: "add_next_layer", label: "Add the next layer" },
  { key: "context_window_slider", type: "context_window_slider", label: "Context window slider" },
  { key: "cliche_highlighter", type: "cliche_highlighter", label: "Cliché highlighter" },
  { key: "tokenizer_playground", type: "tokenizer_playground", label: "Tokenizer playground" },
  { key: "be_the_model", type: "be_the_model", label: "Be the model" },
  { key: "workflow_simulator", type: "workflow_simulator", label: "Workflow simulator" },
  { key: "agent_simulator", type: "agent_simulator", label: "Agent simulator" },
  { key: "dual_chatbot", type: "dual_chatbot", label: "Two chatbots" },
  { key: "data_sandbox", type: "data_sandbox", label: "Data sandbox" },
  { key: "checklist", type: "checklist", label: "Checklist" },
  { key: "free_response", type: "free_response", label: "Written answer" },
  { key: "free_response_card", type: "free_response", label: "Form with a live card", data: structuredClone(CAPABILITY_CARD_EXAMPLE) },
  { key: "quick_question", type: "quick_question", label: "Quick question (each option has its own feedback)" },
  { key: "card_grid", type: "card_grid", label: "Card grid" },
  { key: "compare_columns", type: "compare_columns", label: "Compare columns (side by side)" },
  { key: "tile_reveal", type: "tile_reveal", label: "Tap-to-reveal tiles" },
  { key: "color_sort", type: "color_sort", label: "Colour sort (graded)" },
  { key: "slider_judge", type: "slider_set", label: "Sliders: set and lock in a level" },
  { key: "slider_rate", type: "slider_set", label: "Sliders: rate yourself", data: structuredClone(SLIDER_RATE_EXAMPLE) },
  { key: "scenario_sort", type: "scenario_sort", label: "Scenario sort (pick one, sort, compare with the expert)" },
  { key: "spot_the_errors", type: "spot_the_errors", label: "Spot the errors (flag sentences against a source)" },
  { key: "decision_tree", type: "decision_tree", label: "Decision tree (yes/no questions)" },
  { key: "prompt_builder", type: "prompt_builder", label: "Prompt builder (fields with suggestions)" },
  { key: "project_export", type: "project_export", label: "Download my work (project export)" },
  { key: "filter_compare", type: "filter_compare", label: "Rule vs learned filter (simulator)" },
  { key: "train_model", type: "train_model", label: "Train your own model (simulator)" },
  { key: "meaning_map", type: "meaning_map", label: "Meaning map (simulator)" },
  { key: "mcq", type: "mcq", label: "Multiple choice (one answer)" },
  {
    key: "mcq_multi",
    type: "mcq",
    label: "Multiple choice (several answers)",
    data: { correct_index: undefined, correct_indices: [0] },
  },
  { key: "categorization", type: "categorization", label: "Categorization" },
  { key: "sequencing", type: "sequencing", label: "Sequencing" },
  { key: "fill_blank", type: "fill_blank", label: "Fill in the blanks" },
  { key: "workplace_scenario", type: "workplace_scenario", label: "Workplace scenario (personalized)" },
];

/** A block's name in the canvas, naming its mode where the type has more than one. */
export function blockLabel(block: EditorBlock): string {
  const d = block.data;
  switch (block.type) {
    case "image_hotspot":
      return d.mode === "find" ? "Image hotspot (find)" : BLOCK_LABELS.image_hotspot;
    case "accordion_tabs":
      return d.display === "tabs" ? "Tabs" : "Accordion";
    case "mcq":
      return Array.isArray(d.correct_indices) ? "Multiple choice (several)" : BLOCK_LABELS.mcq;
    default:
      return BLOCK_LABELS[block.type];
  }
}

/** A fresh block of the given type with sensible default data, plus any mode overrides. */
export function makeBlock(type: BlockType, id: string, overrides?: Record<string, unknown>): EditorBlock {
  const block = defaultBlock(type, id);
  if (!overrides) return block;
  return { ...block, data: { ...block.data, ...overrides } };
}

function defaultBlock(type: BlockType, id: string): EditorBlock {
  const base = { id, type, personalized: false };
  switch (type) {
    case "rich_text":
      return { ...base, required_to_advance: false, data: { markdown: "" } };
    case "image":
      return {
        ...base,
        required_to_advance: false,
        data: { image_url: "", alt: "", caption: "", size: "full", align: "center" },
      };
    case "mcq":
      return {
        ...base,
        required_to_advance: true,
        data: { question: "", options: ["", ""], correct_index: 0, points: 10 },
      };
    case "image_hotspot":
      return {
        ...base,
        required_to_advance: true,
        data: {
          image_url: "",
          alt: "",
          // Start with no hotspots — the author adds them (click the image or
          // "Add hotspot"); the schema's min(1) enforces at least one on save.
          hotspots: [],
        },
      };
    case "flip_cards":
      return {
        ...base,
        required_to_advance: true,
        data: { cards: [{ id: crypto.randomUUID(), front: "", back: "" }] },
      };
    case "accordion_tabs":
      return {
        ...base,
        required_to_advance: true,
        data: { display: "accordion", sections: [{ id: crypto.randomUUID(), title: "", body: "" }] },
      };
    case "stepped_timeline":
      return {
        ...base,
        required_to_advance: true,
        data: { steps: [{ id: crypto.randomUUID(), label: "", body: "" }] },
      };
    case "wheel_diagram":
      return {
        ...base,
        required_to_advance: true,
        data: {
          center_label: "",
          slices: [
            { id: crypto.randomUUID(), label: "", body: "" },
            { id: crypto.randomUUID(), label: "", body: "" },
          ],
        },
      };
    case "nested_layers":
      return {
        ...base,
        required_to_advance: true,
        data: {
          layers: [
            { id: crypto.randomUUID(), label: "", body: "" },
            { id: crypto.randomUUID(), label: "", body: "" },
          ],
        },
      };
    case "format_switcher":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "",
          prompt: "",
          complete_on: "any",
          options: [
            { id: crypto.randomUUID(), label: "", body: "" },
            { id: crypto.randomUUID(), label: "", body: "" },
          ],
        },
      };
    case "image_switcher":
      return {
        ...base,
        required_to_advance: true,
        data: {
          options: [
            { id: crypto.randomUUID(), label: "", image_url: "", alt: "", caption: "" },
            { id: crypto.randomUUID(), label: "", image_url: "", alt: "", caption: "" },
          ],
        },
      };
    case "vertical_roadmap": {
      const era = crypto.randomUUID();
      return {
        ...base,
        required_to_advance: true,
        data: {
          eras: [{ id: era, name: "", color: "indigo" }],
          events: [
            { id: crypto.randomUUID(), date: "", era_id: era, text: "" },
            { id: crypto.randomUUID(), date: "", era_id: era, text: "" },
          ],
        },
      };
    }
    case "add_next_layer":
      return {
        ...base,
        required_to_advance: true,
        data: {
          layers: [
            { id: crypto.randomUUID(), label: "", body: "" },
            { id: crypto.randomUUID(), label: "", body: "" },
          ],
        },
      };
    case "context_window_slider":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "",
          window_min: 2,
          messages: [
            {
              id: crypto.randomUUID(),
              role: "you",
              text: "Hi! My name is Ayesha and I'm planning a trip to Hunza.",
            },
            {
              id: crypto.randomUUID(),
              role: "ai",
              text: "Nice to meet you, Ayesha! Hunza is beautiful. How can I help?",
            },
            {
              id: crypto.randomUUID(),
              role: "you",
              text: "By the way, what's my name?",
            },
          ],
          remembered_answer:
            'AI: "Your name is Ayesha!" The first message is still inside the window, so the model can see it.',
          forgotten_answer:
            "AI: \"I'm not sure. I don't think you've told me your name.\" The message with your name is outside the window.",
        },
      };
    case "cliche_highlighter":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "",
          sample_text:
            "In today's fast-paced world, I'm thrilled to announce that I have embarked on an exciting new journey as a Junior Analyst! This role is a testament to my dedication, and I look forward to leveraging my skills to unlock new opportunities and navigate the ever-evolving landscape of logistics.",
          phrases: [
            "In today's fast-paced world",
            "thrilled to announce",
            "embarked on",
            "journey",
            "testament to",
            "leveraging",
            "unlock",
            "ever-evolving",
          ],
        },
      };
    case "tokenizer_playground":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "",
          min_distinct: 2,
          fine_print:
            "These are the real splits from GPT-4o's tokenizer. Other models learn their own chunks, so their splits differ a little. A dot (·) shows a space that belongs to the token.",
          examples: [
            {
              id: crypto.randomUUID(),
              label: "English sentence",
              text: "Artificial intelligence is transforming how we learn.",
              tokens: ["Artificial", " intelligence", " is", " transforming", " how", " we", " learn", "."],
            },
            {
              id: crypto.randomUUID(),
              label: "Long words",
              text: "unbelievably overthinking",
              tokens: ["un", "bel", "ievably", " over", "thinking"],
            },
            {
              id: crypto.randomUUID(),
              label: "Numbers",
              text: "The order total is 1234567 rupees.",
              tokens: ["The", " order", " total", " is", " ", "123", "456", "7", " ru", "pees", "."],
            },
            {
              id: crypto.randomUUID(),
              label: "Urdu",
              text: "مجھے مصنوعی ذہانت سیکھنی ہے",
              tokens: ["مج", "ھے", " مصنوع", "ی", " ذہ", "انت", " س", "یکھ", "نی", " ہے"],
            },
            {
              id: crypto.randomUUID(),
              label: "Arabic",
              text: "أريد أن أتعلم الذكاء الاصطناعي",
              tokens: ["أ", "ريد", " أن", " أ", "تع", "لم", " الذ", "ك", "اء", " الاص", "ط", "نا", "عي"],
            },
          ],
        },
      };
    case "dual_chatbot":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "Try two chatbots",
          subtitle:
            "Both of these bots work for the same (made-up) café. One follows keyword rules. The other is powered by an LLM. Ask them the same questions and compare.",
          rule_panel_title: "Rule-based chatbot",
          rule_panel_sub: "Matches keywords to pre-written replies",
          llm_panel_title: "LLM-powered chatbot",
          llm_panel_sub: "Generates a reply from the café's information",
          fine_print:
            "Live AI replies aren't available here, so the LLM chatbot shows pre-generated replies for the four example questions.",
          min_asks: 2,
          failure_reply:
            "Sorry, I didn't understand. Please type one of: HOURS, MENU, PRICES, LOCATION, BOOKING.",
          presets: [
            "What time do you open?",
            "Do you have anything without gluten for my son?",
            "I'm vegetarian. What goes well with chai?",
            "Can I book a table for 12 on Friday evening?",
          ],
          rules: [
            {
              id: "hours",
              pattern: "open|opening|close|closing|hours|time|timing",
              reply: "Our hours are 8am–11pm, every day.",
            },
            {
              id: "book",
              pattern: "book|booking|reserve|reservation",
              reply: "For reservations, call 051-000-0000.",
            },
            {
              id: "menu",
              pattern: "menu|food|eat|dishes",
              reply: "Menu: chai, parathas, chapati rolls, chaat, kheer.",
            },
            {
              id: "price",
              pattern: "price|prices|cost|much",
              reply: "Prices range from Rs 250 to Rs 650.",
            },
            {
              id: "where",
              pattern: "where|location|address|located",
              reply: "We're in Blue Area, Islamabad.",
            },
          ],
          canned_llm: [
            {
              question: "what time do you open?",
              reply: "We're open every day from 8am to 11pm. See you soon! ☕",
            },
            {
              question: "do you have anything without gluten for my son?",
              reply:
                "Yes! Our rice kheer, fruit chaat and chana chaat are all gluten-free. Just avoid the parathas, chapatis and rolls, since they're made with wheat.",
            },
            {
              question: "i'm vegetarian. what goes well with chai?",
              reply:
                "Lovely choice! An aloo paratha or a veg chapati roll goes perfectly with doodh patti chai. For something lighter, try the chana chaat.",
            },
            {
              question: "can i book a table for 12 on friday evening?",
              reply:
                "We'd love to have your group! For 12 people you'll need to reserve by phone on 051-000-0000. I can't make the booking myself, but the team will sort it out for you.",
            },
          ],
        },
      };
    case "data_sandbox":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "Talk to your data",
          subtitle:
            "Pick a dataset, look at the chart or table, then judge each AI claim. Is the AI right or wrong about the numbers?",
          min_judgments: 6,
          datasets: structuredClone(DEFAULT_DATA_SANDBOX_DATASETS),
        },
      };
    case "agent_simulator":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "Agent Simulator",
          goal: "Handle today's customer inquiries for Chai Corner catering.",
          inbox:
            'From: Hina K. — "Hi! I need catering for 40 people this Saturday. What would it cost? Also, my last order (#1182) arrived cold, which was disappointing."',
          end_message:
            "🎉 Goal complete. You chose the best action first time in {score} steps. Notice what the good choices had in common: sort first, look things up instead of guessing, stay within authority, get approval before anything involving money goes out, and remember only what's useful.",
          steps: [
            {
              id: "s1",
              think: "This email contains more than one request. What should I do first?",
              actions: [
                {
                  id: "s1a",
                  code: "classify_email()",
                  label: "Sort the email into its separate requests",
                  correct: true,
                  result:
                    "Two intents found: a catering quote request, and a complaint about order #1182.",
                  consequence: "Wrong path.",
                },
                {
                  id: "s1b",
                  code: 'send_reply("Our catering is Rs 1,000 per person!")',
                  label: "Reply straight away with a price",
                  correct: false,
                  result: "Would send a wrong price.",
                  consequence:
                    "You guessed a price without checking. It's wrong, and you ignored the complaint. The customer is now more annoyed.",
                },
                {
                  id: "s1c",
                  code: "issue_refund(order #1182)",
                  label: "Refund the last order immediately",
                  correct: false,
                  result: "Would issue a refund.",
                  consequence:
                    "You acted on the complaint without checking the policy or the order, and ignored the catering request.",
                },
              ],
            },
            {
              id: "s2",
              think: "I need a price for 40 people.",
              observation: "Two intents: catering quote, and complaint about order #1182.",
              actions: [
                {
                  id: "s2a",
                  code: 'lookup_prices("catering")',
                  label: "Look up the official catering price list",
                  correct: true,
                  result: "Catering: Rs 950 per person, minimum 30 people. 40 people = Rs 38,000.",
                  consequence: "Wrong path.",
                },
                {
                  id: "s2b",
                  code: "guess_price()",
                  label: "Estimate a price from similar orders",
                  correct: false,
                  result: "Would guess a price.",
                  consequence:
                    "Guessing prices is how agents make expensive promises. Always use the real source.",
                },
                {
                  id: "s2c",
                  code: "forward_to_manager(all)",
                  label: "Forward everything to the manager",
                  correct: false,
                  result: "Would forward to manager.",
                  consequence:
                    "Safe, but it defeats the point. The agent can handle a price lookup itself.",
                },
              ],
            },
          ],
        },
      };
    case "workflow_simulator":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "Workflow Simulator",
          subtitle:
            "Design the restaurant's feedback automation. Choose one option for each block, then run it with three real test responses.",
          action_label: "📧 Action — Send email",
          perfect_verdict:
            "🎉 A well-designed automation. Instant trigger, useful AI step, a record kept, and complaints routed to a human. Now build it for real in Make.com.",
          imperfect_verdict: "Almost. Fix these, then run it again:",
          slots: [
            {
              id: "trigger",
              emoji: "⚡",
              label: "Trigger",
              options: [
                { id: "t2", label: "New Google Form response", score: 2, result: { text: "⚡ Triggered as soon as the form was submitted." } },
                {
                  id: "t1",
                  label: "Every Monday at 9am",
                  score: 1,
                  issue: "Feedback waits up to a week before anyone sees it.",
                  result: { text: "⚡ Waited until Monday 9am." },
                },
                {
                  id: "t0",
                  label: "New email in your personal inbox",
                  score: 0,
                  issue:
                    "Your personal inbox is full of unrelated mail. The automation will try to reply to everything.",
                  result: { text: "⚡ Mixed in with personal emails. Hard to find." },
                },
              ],
            },
            {
              id: "ai",
              emoji: "🤖",
              label: "AI step",
              options: [
                {
                  id: "a2",
                  label: "Classify as complaint, question or praise, and draft a reply",
                  score: 2,
                  result: { text: "🤖 AI classified it as: {type}, and drafted a reply." },
                },
                {
                  id: "a0a",
                  label: "Calculate the average rating",
                  score: 0,
                  issue:
                    "That's maths. A formula does it perfectly, and it doesn't help you reply.",
                  result: { text: "🤖 Calculate the average rating: no useful reply drafted." },
                },
                {
                  id: "a0b",
                  label: "Write a funny poem about the feedback",
                  score: 0,
                  issue: "Not what a customer needs.",
                  result: { text: "🤖 Write a funny poem about the feedback: no useful reply drafted." },
                },
              ],
            },
            {
              id: "tool",
              emoji: "🔧",
              label: "Tool",
              options: [
                { id: "tool2", label: "Add a row to a Google Sheet", score: 2, result: { text: "🔧 Saved to the feedback sheet." } },
                {
                  id: "tool0a",
                  label: "Delete the form response",
                  score: 0,
                  issue: "You'd lose the record of what the customer said.",
                  result: { text: "🔧 Response deleted. The record is gone." },
                },
                {
                  id: "tool0b",
                  label: "Post the feedback publicly on Facebook",
                  score: 0,
                  issue:
                    "That publishes customers' words without permission. A privacy problem.",
                  result: { text: "🔧 Posted publicly on Facebook." },
                },
              ],
            },
            {
              id: "router",
              emoji: "🔀",
              label: "Router",
              blocked_by: "ai",
              blocked_result: "🔀 Nothing sensible to route.",
              options: [
                {
                  id: "r2",
                  label: "Complaints → staff review; everything else → auto-reply",
                  score: 2,
                  result: { text: "🔀 Auto-reply sent within a minute." },
                  results_by_type: { complaint: { text: "🔀 Complaint → sent to staff to review before replying." } },
                },
                {
                  id: "r0",
                  label: "Auto-reply to everything",
                  score: 0,
                  issue:
                    "Angry customers get an automatic, possibly wrong, reply to a serious complaint.",
                  result: { text: "🔀 Auto-reply sent.", ok: true },
                  results_by_type: {
                    complaint: { text: "🔀 An automatic reply went to an angry customer asking for a refund. No human saw it." },
                  },
                },
                {
                  id: "r1",
                  label: "Send everything to staff to reply manually",
                  score: 1,
                  issue: "Safe, but it saves almost no time.",
                  result: { text: "🔀 Sent to staff. The customer waits for someone to reply by hand." },
                  results_by_type: {
                    complaint: { text: "🔀 Sent to staff. The customer waits for someone to reply by hand.", ok: true },
                  },
                },
              ],
            },
          ],
          tests: [
            {
              id: "praise",
              message: '⭐ "Loved the biryani! Best in town."',
              type: "praise",
            },
            {
              id: "question",
              message: '❓ "Do you deliver to DHA Phase 6?"',
              type: "question",
            },
            {
              id: "complaint",
              message: '😠 "My order was an hour late and cold. I want my money back."',
              type: "complaint",
            },
          ],
        },
      };
    case "be_the_model":
      return {
        ...base,
        required_to_advance: true,
        data: {
          title: "Be the model",
          subtitle:
            "Generate the rest of the sentence. Tap a word to pick it yourself, or let the model pick.",
          prompt: "My favourite thing to eat for breakfast is",
          temperature_min: 0.1,
          temperature_max: 2,
          temperature_default: 1,
          max_words: 6,
          min_sentences: 3,
          insight_both_temps:
            'You\'ve tried both ends. At low temperature the sentences barely change. At high temperature you get variety, and oddities like "sandpaper" or "gravel". The model never "knew" what you eat. It just picked likely words.',
          insight_hint:
            "Try this: generate 2 sentences at temperature 0.2, then 2 at 1.8. Compare how similar they are.",
          transitions: [
            {
              after: "START",
              options: [
                { word: "paratha", p: 0.3 },
                { word: "eggs", p: 0.22 },
                { word: "toast", p: 0.15 },
                { word: "halwa puri", p: 0.11 },
                { word: "cereal", p: 0.08 },
                { word: "fruit", p: 0.07 },
                { word: "leftover biryani", p: 0.05 },
                { word: "sandpaper", p: 0.02 },
              ],
            },
            {
              after: "paratha",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "eggs",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "toast",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "halwa puri",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "cereal",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "fruit",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "leftover biryani",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "sandpaper",
              options: [
                { word: "with", p: 0.42 },
                { word: "and", p: 0.26 },
                { word: "because", p: 0.18 },
                { word: ".", p: 0.14 },
              ],
            },
            {
              after: "with",
              options: [
                { word: "chai", p: 0.44 },
                { word: "tea", p: 0.18 },
                { word: "yogurt", p: 0.14 },
                { word: "butter", p: 0.12 },
                { word: "ketchup", p: 0.09 },
                { word: "gravel", p: 0.03 },
              ],
            },
            {
              after: "and",
              options: [
                { word: "chai", p: 0.38 },
                { word: "an omelette", p: 0.2 },
                { word: "fresh juice", p: 0.17 },
                { word: "a long nap", p: 0.15 },
                { word: "regret", p: 0.1 },
              ],
            },
            {
              after: "because",
              options: [
                { word: "it", p: 0.46 },
                { word: "my mother", p: 0.24 },
                { word: "nothing", p: 0.18 },
                { word: "the sky", p: 0.12 },
              ],
            },
            {
              after: "it",
              options: [
                { word: "keeps me full", p: 0.42 },
                { word: "tastes like home", p: 0.34 },
                { word: "is quick", p: 0.2 },
                { word: "sings", p: 0.04 },
              ],
            },
            {
              after: "my mother",
              options: [
                { word: "makes it", p: 0.6 },
                { word: "loves it", p: 0.3 },
                { word: "says so", p: 0.1 },
              ],
            },
            {
              after: "nothing",
              options: [
                { word: "beats it", p: 0.8 },
                { word: "else compares", p: 0.2 },
              ],
            },
            {
              after: "the sky",
              options: [
                { word: "is blue", p: 0.6 },
                { word: "told me", p: 0.4 },
              ],
            },
          ],
        },
      };
    case "checklist":
      return {
        ...base,
        required_to_advance: true,
        data: { items: [{ id: crypto.randomUUID(), label: "" }] },
      };
    case "free_response":
      return { ...base, required_to_advance: true, data: { prompt: "", min_words: 20 } };
    // These start from the lesson they were built for; authors edit the data as JSON.
    case "quick_question":
      return { ...base, required_to_advance: true, data: structuredClone(QUICK_QUESTION_EXAMPLE) };
    case "card_grid":
      return { ...base, required_to_advance: false, data: structuredClone(CARD_GRID_EXAMPLE) };
    case "compare_columns":
      return { ...base, required_to_advance: false, data: structuredClone(COMPARE_COLUMNS_EXAMPLE) };
    case "tile_reveal":
      return { ...base, required_to_advance: false, data: structuredClone(TILE_REVEAL_EXAMPLE) };
    case "color_sort":
      return { ...base, required_to_advance: true, data: structuredClone(COLOR_SORT_EXAMPLE) };
    case "slider_set":
      return { ...base, required_to_advance: true, data: structuredClone(SLIDER_JUDGE_EXAMPLE) };
    case "scenario_sort":
      return { ...base, required_to_advance: true, data: structuredClone(SCENARIO_SORT_EXAMPLE) };
    case "spot_the_errors":
      return { ...base, required_to_advance: true, data: structuredClone(SPOT_THE_ERRORS_EXAMPLE) };
    case "decision_tree":
      return { ...base, required_to_advance: true, data: structuredClone(DECISION_TREE_EXAMPLE) };
    case "prompt_builder":
      return { ...base, required_to_advance: true, data: structuredClone(PROMPT_BUILDER_EXAMPLE) };
    case "project_export":
      return { ...base, required_to_advance: false, data: structuredClone(PROJECT_EXPORT_EXAMPLE) };
    case "filter_compare":
      return { ...base, required_to_advance: true, data: structuredClone(SPAM_FILTER_EXAMPLE) };
    case "train_model":
      return { ...base, required_to_advance: true, data: structuredClone(TRAIN_MODEL_EXAMPLE) };
    case "meaning_map":
      return { ...base, required_to_advance: true, data: structuredClone(MEANING_MAP_EXAMPLE) };
    case "categorization": {
      const b1 = crypto.randomUUID();
      const b2 = crypto.randomUUID();
      return {
        ...base,
        required_to_advance: true,
        data: {
          buckets: [
            { id: b1, label: "" },
            { id: b2, label: "" },
          ],
          items: [{ id: crypto.randomUUID(), label: "", correct_bucket_id: b1 }],
          points_per_match: 5,
        },
      };
    }
    case "sequencing": {
      const i1 = crypto.randomUUID();
      const i2 = crypto.randomUUID();
      return {
        ...base,
        required_to_advance: true,
        data: {
          items: [
            { id: i1, label: "" },
            { id: i2, label: "" },
          ],
          correct_order: [i1, i2],
          points: 15,
        },
      };
    }
    case "fill_blank":
      return {
        ...base,
        required_to_advance: true,
        data: {
          template: "",
          blanks: [{ id: "blank1", options: ["", ""], correct_index: 0 }],
          points_per_blank: 5,
        },
      };
    case "workplace_scenario":
      return {
        id,
        type,
        personalized: true,
        required_to_advance: false,
        data: {
          component_prompt: "",
          metadata_fields: ["domain", "department", "future_goal"],
        },
      };
    default:
      // Types without a form yet — kept here so the union stays exhaustive.
      return { ...base, required_to_advance: true, data: {} };
  }
}

/** A section groups blocks into one navigable part (slide). */
export type EditorSection = {
  id: string;
  title: string;
  required_to_advance: boolean;
  blocks: EditorBlock[];
};

/** A fresh, empty section — gated by default. */
export function makeSection(id: string): EditorSection {
  return { id, title: "", required_to_advance: true, blocks: [] };
}
